/**
 * Notification Center Component
 *
 * Displays shore edit notifications and allows acknowledgement.
 * Part of the Fleet Dashboard.
 */

import React, { useState } from 'react';
import {
  Bell,
  Check,
  MessageSquare,
  AlertTriangle,
  AlertCircle,
  Info,
  Clock,
  Ship,
  ChevronDown,
  ChevronUp,
  Filter
} from 'lucide-react';

import { useFleetNotifications } from '../../../../hooks/domain/fleet/useFleetNotifications';
import { usePermissions } from '../../../../hooks/usePermissions';

/**
 * Priority badge component
 */
const PriorityBadge = ({ priority }) => {
  const config = {
    critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'Critical' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
    medium: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Medium' },
    low: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Low' }
  };

  const { bg, text, label } = config[priority] || config.medium;

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${bg} ${text}`}>
      {label}
    </span>
  );
};

/**
 * Single notification item
 */
const NotificationItem = ({ notification, onAcknowledge, onQuery, expanded, onToggle }) => {
  const [queryText, setQueryText] = useState('');
  const [showQueryInput, setShowQueryInput] = useState(false);

  const isAcknowledged = !!notification.acknowledgedAt;
  const isQueried = notification.acknowledgementType === 'queried';

  const handleQuery = () => {
    if (queryText.trim()) {
      onQuery(notification.id, queryText);
      setShowQueryInput(false);
      setQueryText('');
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className={`
      bg-white rounded-lg border overflow-hidden
      ${isAcknowledged ? 'border-slate-200 opacity-75' : 'border-slate-300 shadow-sm'}
    `}>
      {/* Header */}
      <div
        className={`px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-slate-50 ${
          notification.priority === 'critical' ? 'bg-red-50' :
          notification.priority === 'high' ? 'bg-orange-50' : ''
        }`}
        onClick={onToggle}
      >
        {/* Icon */}
        <div className={`
          p-2 rounded-full flex-shrink-0
          ${notification.priority === 'critical' ? 'bg-red-100' :
            notification.priority === 'high' ? 'bg-orange-100' : 'bg-amber-100'}
        `}>
          {notification.priority === 'critical' ? (
            <AlertTriangle className="h-4 w-4 text-red-600" />
          ) : notification.priority === 'high' ? (
            <AlertCircle className="h-4 w-4 text-orange-600" />
          ) : (
            <Info className="h-4 w-4 text-amber-600" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="font-medium text-slate-800 truncate">
                {notification.title}
              </h4>
              <p className="text-sm text-slate-500 flex items-center gap-2 mt-0.5">
                <Ship className="h-3 w-3" />
                {notification.vesselName || 'Unknown Vessel'}
                <span className="text-slate-300">•</span>
                <Clock className="h-3 w-3" />
                {getTimeAgo(notification.editedAt)}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <PriorityBadge priority={notification.priority} />
              {isAcknowledged && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <Check className="h-3 w-3" />
                  Acknowledged
                </span>
              )}
              {isQueried && (
                <span className="flex items-center gap-1 text-xs text-amber-600">
                  <MessageSquare className="h-3 w-3" />
                  Queried
                </span>
              )}
              {expanded ? (
                <ChevronUp className="h-4 w-4 text-slate-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
          {/* Message */}
          <p className="text-sm text-slate-600 mb-3">
            {notification.message}
          </p>

          {/* Change Details */}
          {notification.change && (
            <div className="bg-white rounded border border-slate-200 p-3 mb-3">
              <p className="text-xs text-slate-500 mb-2">Change Details</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-500">Module:</span>{' '}
                  <span className="font-medium">{notification.change.module}</span>
                </div>
                <div>
                  <span className="text-slate-500">Item:</span>{' '}
                  <span className="font-medium">{notification.change.itemName || notification.change.field}</span>
                </div>
                <div>
                  <span className="text-slate-500">Previous:</span>{' '}
                  <span className="font-medium text-red-600">{notification.change.oldValue}</span>
                </div>
                <div>
                  <span className="text-slate-500">New:</span>{' '}
                  <span className="font-medium text-green-600">{notification.change.newValue}</span>
                </div>
              </div>
              {notification.change.reason && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <span className="text-slate-500 text-sm">Reason:</span>{' '}
                  <span className="text-sm">{notification.change.reason}</span>
                </div>
              )}
            </div>
          )}

          {/* Edited By */}
          <p className="text-xs text-slate-500 mb-3">
            Changed by: {notification.editedBy?.name} ({notification.editedBy?.role})
            on {new Date(notification.editedAt).toLocaleString()}
          </p>

          {/* Query Response */}
          {notification.query?.queryText && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3">
              <p className="text-xs text-amber-700 font-medium mb-1">Query Raised:</p>
              <p className="text-sm text-amber-800">{notification.query.queryText}</p>
              {notification.query.responseText && (
                <>
                  <p className="text-xs text-amber-700 font-medium mt-2 mb-1">Response:</p>
                  <p className="text-sm text-amber-800">{notification.query.responseText}</p>
                </>
              )}
            </div>
          )}

          {/* Query Input */}
          {showQueryInput && (
            <div className="mb-3">
              <textarea
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                placeholder="Describe your concern with this change..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                rows={3}
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => {
                    setShowQueryInput(false);
                    setQueryText('');
                  }}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleQuery}
                  disabled={!queryText.trim()}
                  className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                >
                  Submit Query
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          {!isAcknowledged && !showQueryInput && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onAcknowledge(notification.id)}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
              >
                <Check className="h-4 w-4" />
                Acknowledge
              </button>
              <button
                onClick={() => setShowQueryInput(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-100 text-amber-700 text-sm font-medium rounded-lg hover:bg-amber-200"
              >
                <MessageSquare className="h-4 w-4" />
                Query Change
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Notification Center Main Component
 */
const NotificationCenter = () => {
  const {
    notifications,
    unacknowledgedNotifications,
    acknowledgeNotification,
    queryNotification,
    getFleetNotifications
  } = useFleetNotifications();
  const { isFleet } = usePermissions();

  const [filter, setFilter] = useState('unacknowledged'); // all, unacknowledged, queried
  const [expandedId, setExpandedId] = useState(null);

  // Get appropriate notifications based on context
  const allNotifications = isFleet ? getFleetNotifications() : notifications;

  // Filter notifications
  const filteredNotifications = allNotifications.filter(n => {
    if (filter === 'unacknowledged') return !n.acknowledgedAt;
    if (filter === 'queried') return n.acknowledgementType === 'queried';
    return true;
  });

  const handleAcknowledge = (notificationId) => {
    acknowledgeNotification(notificationId);
    setExpandedId(null);
  };

  const handleQuery = (notificationId, queryText) => {
    queryNotification(notificationId, queryText);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notifications
          </h2>
          <p className="text-sm text-slate-500">
            {unacknowledgedNotifications.length} unacknowledged
          </p>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-cyan-500"
          >
            <option value="unacknowledged">Unacknowledged</option>
            <option value="queried">Queried</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">No Notifications</h3>
          <p className="text-slate-500">
            {filter === 'unacknowledged'
              ? 'All shore edit notifications have been acknowledged.'
              : filter === 'queried'
              ? 'No notifications are currently queried.'
              : 'No notifications to display.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onAcknowledge={handleAcknowledge}
              onQuery={handleQuery}
              expanded={expandedId === notification.id}
              onToggle={() => setExpandedId(
                expandedId === notification.id ? null : notification.id
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
